import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { type RunStepOut } from "@platform/api/golden";
import { Alert, Button, Card, Chip, Typography } from "@platform/design-system";
import { useState } from "react";

import { formatTimestamp } from "../shared/values";

import { type RunStream, type StreamConnection } from "./useRunStream";

import type { ReactNode } from "react";

export const RUN_STEPS_TEST_ID = "golden-run-steps";

const shortId = (id: string | null | undefined): string =>
  id ? id.slice(0, 8) : "";

/** What a tool call was about: a coverage or document by its short id, else its section. */
const targetLabel = (step: RunStepOut): string => {
  const target = step.target;
  if (!target) return "";
  const id = target.coverage_id ?? target.document_id;
  return id ? `${target.type} ${shortId(id)}` : target.type;
};

const planLabel = (step: RunStepOut): string => {
  const plan = step.detail?.benefit_plan;
  if (!plan) return "";
  if (step.name === "set_commission" && plan.coverage_type)
    return plan.coverage_type;
  return [plan.identifier ?? `plan ${shortId(plan.id)}`, plan.plan_type]
    .filter(Boolean)
    .join(" ");
};

const pagesLabel = (pages?: string, total?: number): string => {
  const of = total === undefined ? "" : ` of ${total}`;
  if (pages === "all")
    return total === undefined ? "all pages" : `all ${total} pages`;
  if (pages) return `pages ${pages}${of}`;
  return total === undefined ? "" : `${total} pages`;
};

/**
 * What a step was about: the page, query, document or plan a tool call named,
 * or the tools a model turn asked for and why it ended when not to call them.
 */
const detailLabel = (step: RunStepOut): string => {
  const detail = step.detail;
  if (!detail) return "";
  if (step.kind === "model_turn") {
    const asked = detail.requested_tools?.length
      ? `asked for ${detail.requested_tools.join(", ")}`
      : "";
    const ended =
      detail.stop_reason && detail.stop_reason !== "tool_call"
        ? `ended: ${detail.stop_reason}`
        : "";
    return [asked, ended].filter(Boolean).join("; ");
  }
  if (detail.knowledge_page_title) return detail.knowledge_page_title;
  if (detail.search_query) return `"${detail.search_query}"`;
  if (detail.document_name) {
    const pages = pagesLabel(detail.pages, detail.total_pages);
    return pages ? `${detail.document_name}, ${pages}` : detail.document_name;
  }
  return [
    planLabel(step),
    detail.rate_category,
    detail.rate_designs?.join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
};

type StepParams = NonNullable<NonNullable<RunStepOut["detail"]>["params"]>;

/** A call's arguments that are a single value, such as coverageType: dental, in one line. */
const paramsSummary = (params: StepParams): string =>
  Object.entries(params)
    .filter(([, value]) => value === null || typeof value !== "object")
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(" · ");

/**
 * Every argument a tool call passed: a one-line summary, and the whole JSON on demand.
 *
 * PHI: the arguments can carry RFP and member data; they are only rendered,
 * never logged or stored by the UI.
 */
const StepParamsView = ({
  seq,
  params,
}: {
  readonly seq: number;
  readonly params: StepParams;
}): ReactNode => {
  const [shown, setShown] = useState(false);
  const summary = paramsSummary(params);
  return (
    <Box data-testid={`${RUN_STEPS_TEST_ID}-params-${seq}`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {summary && (
          <Typography
            variant="caption"
            color="textSecondary"
            data-testid={`${RUN_STEPS_TEST_ID}-params-summary-${seq}`}
          >
            {summary}
          </Typography>
        )}
        <Button
          variant="tertiary"
          size="sm"
          data-testid={`${RUN_STEPS_TEST_ID}-params-toggle-${seq}`}
          onClick={() => setShown(!shown)}
        >
          {shown ? "Hide params" : "Show params"}
        </Button>
      </Box>
      {shown && (
        <Box
          component="pre"
          data-testid={`${RUN_STEPS_TEST_ID}-params-json-${seq}`}
          sx={{
            m: 0,
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(params, null, 2)}
        </Box>
      )}
    </Box>
  );
};

const durationLabel = (ms: number): string =>
  ms < 1_000 ? `${ms} ms` : `${(ms / 1_000).toFixed(1)} s`;

const tokensLabel = (step: RunStepOut): string => {
  const usage = step.usage;
  if (!usage) return "";
  const cached = usage.cache_read > 0 ? `, ${usage.cache_read} cached` : "";
  return `${usage.input} in, ${usage.output} out${cached}`;
};

const StepRow = ({ step }: { readonly step: RunStepOut }): ReactNode => {
  const about = detailLabel(step) || targetLabel(step);
  const params = step.detail?.params;
  return (
    <TableRow data-testid={`${RUN_STEPS_TEST_ID}-step-${step.seq}`}>
      <TableCell>{step.seq}</TableCell>
      <TableCell>
        {step.kind === "model_turn" ? "Model turn" : <code>{step.name}</code>}
        {about && (
          <Typography
            variant="caption"
            color="textSecondary"
            component="div"
            data-testid={`${RUN_STEPS_TEST_ID}-about-${step.seq}`}
          >
            {about}
          </Typography>
        )}
        {params && Object.keys(params).length > 0 && (
          <StepParamsView seq={step.seq} params={params} />
        )}
      </TableCell>
      <TableCell>
        {step.failed ? (
          <Chip
            size="small"
            variant="subtle"
            leadingVisual={false}
            label={step.error_code || "failed"}
          />
        ) : (
          "ok"
        )}
      </TableCell>
      <TableCell>{durationLabel(step.duration_ms)}</TableCell>
      <TableCell>{tokensLabel(step)}</TableCell>
      <TableCell>{formatTimestamp(step.started_at)}</TableCell>
    </TableRow>
  );
};

const FOLLOWING: Partial<Record<StreamConnection, string>> = {
  connecting: "Connecting to the run",
  live: "Following RUMI as it works",
  reconnecting: "Connection lost; reconnecting",
};

/**
 * What RUMI has done on the run: each tool call and model turn, as it happens
 * while the run is going, with what each was about: the page, document, plan
 * or search query it named, and every argument a tool call passed. No text
 * RUMI wrote and no tool's result is shown.
 */
export const RunSteps = ({
  stream,
}: {
  readonly stream: RunStream;
}): ReactNode => {
  const { steps, state, connection, droppedEvents } = stream;
  const ended = connection === "ended";
  const following =
    connection === "live" && state === "queued"
      ? "Waiting for RUMI to start"
      : FOLLOWING[connection];

  return (
    <Card data-testid={RUN_STEPS_TEST_ID}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="header-small" component="h2">
          RUMI steps
        </Typography>
        {following && (
          <Box
            data-testid={`${RUN_STEPS_TEST_ID}-following`}
            data-connection={connection}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <CircularProgress size={16} aria-label="Following the run" />
            <Typography variant="caption" color="textSecondary">
              {following}
            </Typography>
          </Box>
        )}
      </Box>
      {connection === "unavailable" && (
        <Alert
          data-testid={`${RUN_STEPS_TEST_ID}-unavailable`}
          severity="warning"
          sx={{ mb: 2 }}
        >
          {
            "The run's steps can't be followed right now. Reload the page to try again; the run goes on either way."
          }
        </Alert>
      )}
      {droppedEvents && (
        <Alert
          data-testid={`${RUN_STEPS_TEST_ID}-dropped`}
          severity="warning"
          sx={{ mb: 2 }}
        >
          {"Some of the run's updates could not be read and were skipped."}
        </Alert>
      )}
      {steps.length === 0 ? (
        <Typography
          variant="body-medium"
          color="textSecondary"
          data-testid={`${RUN_STEPS_TEST_ID}-empty`}
        >
          {ended ? "RUMI recorded no steps for this run." : "No steps yet."}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Step</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>Took</TableCell>
              <TableCell>Tokens</TableCell>
              <TableCell>Started</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {steps.map((step) => (
              <StepRow key={step.seq} step={step} />
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};
